import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getResources } from '../lib/api-client';
import { FilterBar } from '../components/FilterBar';
import { ResourceModal } from '../components/ResourceModal';
import { deserialize } from '@shared/filter-serializer';
import type { FilterState, NetworkResource, Severity } from '@shared/types';
import { cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Server, Loader2, AlertTriangle, PackageX, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { Server, Loader2, AlertTriangle, Package, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 25;

interface ResourcesState {
  resources: NetworkResource[];
  total: number;
  page: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  stale: boolean;
}

export function InventoryTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => deserialize(searchParams));
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [filterKey, setFilterKey] = useState(0);
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [state, setState] = useState<ResourcesState>({
    resources: [],
    total: 0,
    page: 1,
    totalPages: 0,
    loading: true,
    error: null,
    stale: false,
  });

  const fetchResources = useCallback(
    async (page: number, currentFilters: FilterState) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const params: Record<string, string | number | undefined> = {
          page,
          pageSize: PAGE_SIZE,
          sort: sortField,
          order: sortOrder,
        };

        // Apply filters
        if (currentFilters.providers.length > 0) {
          params.provider = currentFilters.providers.join(',');
        }
        if (currentFilters.resourceTypes.length > 0) {
          params.type = currentFilters.resourceTypes.join(',');
        }
        if (currentFilters.regions.length > 0) {
          params.region = currentFilters.regions.join(',');
        }
        if (currentFilters.projectsOrAccounts.length > 0) {
          params.project = currentFilters.projectsOrAccounts.join(',');
        }
        if (currentFilters.environments.length > 0) {
          params.environment = currentFilters.environments.join(',');
        }
        if (currentFilters.severities.length > 0) {
          params.severity = currentFilters.severities.join(',');
        }
        // Only apply search with 2+ characters
        if (currentFilters.search.length >= 2) {
          params.search = currentFilters.search;
        }

        const response = await getResources(params);
        const pageData = response.data;

        setState({
          resources: pageData?.data ?? [],
          total: pageData?.total ?? 0,
          page: pageData?.page ?? 1,
          totalPages: pageData?.totalPages ?? 0,
          loading: false,
          error: null,
          stale: response.stale,
        });
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load resources',
        }));
      }
    },
    [sortField, sortOrder]
  );

  // Fetch on initial load and when filters or sort change
  useEffect(() => {
    fetchResources(1, filters);
  }, [filters, fetchResources]);

  const handleSort = useCallback((field: string) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return field;
    });
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const handleClearAll = useCallback(() => {
    const emptyFilters: FilterState = {
      providers: [],
      resourceTypes: [],
      regions: [],
      projectsOrAccounts: [],
      environments: [],
      severities: [],
      search: '',
    };
    setFilters(emptyFilters);
    setSearchParams(new URLSearchParams(), { replace: true });
    setFilterKey((k) => k + 1);
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      fetchResources(newPage, filters);
    },
    [fetchResources, filters]
  );

  const handleRowClick = useCallback(
    (resource: NetworkResource) => {
      setSelectedResourceId(resource.id);
    },
    []
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/20">
          <Server className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Inventario</h1>
          <p className="text-sm text-gray-500">Explora y busca todos los recursos de red descubiertos.</p>
        </div>
      </div>

      {/* Stale data banner */}
      {state.stale && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
          <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-xs font-medium text-amber-700">Datos en caché</span>
        </div>
      )}

      {/* Filter bar */}
      <FilterBar
        key={filterKey}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        showSeverity={false}
      />

      {/* Error state */}
      {state.error && (
        <Card className="border-red-200">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" />
            <p className="text-sm text-red-800">{state.error}</p>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {state.loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            <p className="text-sm font-medium text-gray-400">Cargando inventario…</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!state.loading && !state.error && state.resources.length === 0 && (
        <EmptyState onClearFilters={handleClearAll} />
      )}

      {/* Table */}
      {!state.loading && !state.error && state.resources.length > 0 && (
        <>
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/80">
                  <tr>
                    <SortableHeader field="name" label="Name" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="type" label="Type" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="provider" label="Provider" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="region" label="Region" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="project" label="Project / Account" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="status" label="Status" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {state.resources.map((resource) => (
                    <tr
                      key={resource.id}
                      onClick={() => handleRowClick(resource)}
                      className="cursor-pointer transition-colors hover:bg-gray-50/80 group"
                      role="link"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(resource);
                        }
                      }}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <SeverityIndicator resource={resource} />
                          <span className="font-medium text-gray-900 group-hover:text-primary-600">
                            {resource.name}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium">
                          {formatResourceType(resource.resourceType)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        <ProviderBadge provider={resource.provider} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {resource.region ?? 'Global'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {resource.projectOrAccount}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <StatusBadge status={resource.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Pagination */}
          <Pagination
            page={state.page}
            totalPages={state.totalPages}
            total={state.total}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Resource Detail Modal */}
      {selectedResourceId && (
        <ResourceModal
          resourceId={selectedResourceId}
          onClose={() => setSelectedResourceId(null)}
        />
      )}
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────────── */

function EmptyState({ onClearFilters }: { onClearFilters: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <Package className="h-12 w-12 text-gray-300" />
        <h3 className="mt-4 text-sm font-medium text-gray-900">No resources found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No resources match the current filters. Try adjusting your search or filters.
        </p>
        <Button
          variant="primary"
          className="mt-4"
          onClick={onClearFilters}
        >
          Clear all filters
        </Button>
      </CardContent>
    </Card>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-gray-600">
        <span className="font-medium">{total}</span> resources total
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Previous
        </Button>
        <span className="text-sm text-gray-600">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function SeverityIndicator({ resource }: { resource: NetworkResource }) {
  const severity = resource.metadata?.highestSeverity as Severity | undefined;
  if (!severity || (severity !== 'critical' && severity !== 'high')) {
    return null;
  }

  const colors: Record<string, string> = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
  };

  return (
    <span
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${colors[severity]}`}
      title={`${severity} severity finding`}
      aria-label={`${severity} severity finding`}
    />
  );
}

function ProviderBadge({ provider }: { provider: 'gcp' | 'aws' }) {
  const variant = provider === 'gcp' ? 'info' : 'warning';

  return (
    <Badge variant={variant}>
      {provider.toUpperCase()}
    </Badge>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-50 text-green-700',
    deleted: 'bg-red-50 text-red-700',
    unknown: 'bg-gray-100 text-gray-600',
  };

  return (
    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${styles[status] ?? styles.unknown}`}>
      {status}
    </span>
  );
}

function formatResourceType(type: string): string {
  return type.replace(/_/g, ' ');
}

function SortableHeader({
  field,
  label,
  currentSort,
  currentOrder,
  onSort,
}: {
  field: string;
  label: string;
  currentSort: string;
  currentOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
}) {
  const isActive = currentSort === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer select-none hover:text-gray-700 hover:bg-gray-100 transition-colors"
      onClick={() => onSort(field)}
      aria-sort={isActive ? (currentOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className={`inline-flex flex-col ${isActive ? 'text-primary-600' : 'text-gray-300'}`}>
          <svg className={`h-3 w-3 ${isActive && currentOrder === 'asc' ? 'text-primary-600' : ''}`} viewBox="0 0 10 5" fill="currentColor">
            <path d="M5 0L10 5H0z" />
          </svg>
          <svg className={`h-3 w-3 ${isActive && currentOrder === 'desc' ? 'text-primary-600' : ''}`} viewBox="0 0 10 5" fill="currentColor">
            <path d="M5 5L0 0h10z" />
          </svg>
        </span>
      </div>
    </th>
  );
}
